import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Mail, KeyRound, CheckCircle2 } from "lucide-react";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setSent(true);
      toast.success("Si votre email existe, un lien a été envoyé");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="je-container py-12 max-w-md" data-testid="forgot-password-page">
      <div className="flex items-center gap-2 mb-2">
        <KeyRound className="w-6 h-6 text-[#0052CC]" />
        <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
          Mot de passe oublié
        </h1>
      </div>
      <p className="text-gray-500 mb-6">
        Entrez votre adresse email. Nous vous enverrons un lien pour réinitialiser votre mot de passe.
      </p>

      {sent ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5" data-testid="forgot-password-sent">
          <div className="flex items-center gap-2 text-green-700 font-semibold mb-1">
            <CheckCircle2 className="w-5 h-5" /> Email envoyé
          </div>
          <p className="text-sm text-gray-700">
            Vérifiez votre boîte de réception à <b>{email}</b>. Si aucun email n&apos;arrive dans 5 minutes,
            vérifiez vos spams.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              required
              placeholder="votre@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border rounded-md pl-9 pr-3 py-2.5"
              data-testid="forgot-email"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="je-btn-primary w-full disabled:opacity-50"
            data-testid="forgot-submit"
          >
            {loading ? "Envoi en cours..." : "Recevoir le lien"}
          </button>
        </form>
      )}

      <p className="text-center text-sm text-gray-500 mt-6">
        <Link to="/login" className="text-[#0052CC] font-semibold" data-testid="back-to-login">
          ← Retour à la connexion
        </Link>
      </p>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (!token) {
      toast.error("Lien invalide — demandez un nouveau lien");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, password });
      toast.success("Mot de passe mis à jour ! Vous pouvez vous connecter.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="je-container py-12 max-w-md" data-testid="reset-password-page">
      <div className="flex items-center gap-2 mb-2">
        <KeyRound className="w-6 h-6 text-[#0052CC]" />
        <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
          Nouveau mot de passe
        </h1>
      </div>
      <p className="text-gray-500 mb-6">
        Choisissez un nouveau mot de passe. Il doit contenir au moins 6 caractères.
      </p>

      {!token && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-4 text-sm">
          Lien invalide ou manquant. <Link to="/forgot-password" className="underline font-semibold">Demandez un nouveau lien</Link>.
        </div>
      )}

      <form onSubmit={submit} className="space-y-3">
        <input
          type="password"
          required
          minLength={6}
          placeholder="Nouveau mot de passe (6+ caractères)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full border rounded-md px-3 py-2.5"
          data-testid="reset-password"
        />
        <input
          type="password"
          required
          minLength={6}
          placeholder="Confirmer le mot de passe"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="w-full border rounded-md px-3 py-2.5"
          data-testid="reset-password-confirm"
        />
        <button
          type="submit"
          disabled={loading || !token}
          className="je-btn-primary w-full disabled:opacity-50"
          data-testid="reset-submit"
        >
          {loading ? "Mise à jour..." : "Réinitialiser le mot de passe"}
        </button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-6">
        <Link to="/login" className="text-[#0052CC] font-semibold">
          ← Retour à la connexion
        </Link>
      </p>
    </div>
  );
}
